import React, { useCallback, useEffect, useRef, useState } from "react";
import { Player } from "../api/client";
import {
  Attendance,
  AttendanceWrite,
  Training,
  formatDay,
  formatTimeRange,
  trainingsApi,
  weekdayOf,
} from "../api/trainings";

// The app offers 0–5 points per training.
const MAX_POINTS = 5;

type Mark = { attended: boolean; points: number };

const ABSENT: Mark = { attended: false, points: 0 };

// The app shows an absent child's points as 0 whatever the column holds.
const markOf = (record: Attendance | undefined): Mark =>
  record ? { attended: record.attended, points: record.attended ? record.points : 0 } : ABSENT;

const byName = (a: Player, b: Player) => a.name.localeCompare(b.name, "ru");

function clampPoints(value: string): number {
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, MAX_POINTS);
}

type Props = { training: Training; onClose: () => void };

export default function TrainingAttendance({ training, onClose }: Props) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [records, setRecords] = useState<Record<string, Attendance>>({});
  const [marks, setMarks] = useState<Record<string, Mark>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const panelRef = useRef<HTMLElement>(null);
  const groupID = training.group_id;

  const load = useCallback(async () => {
    if (!groupID) return;
    const [playerList, attendance] = await Promise.all([
      trainingsApi.listGroupPlayers(groupID),
      trainingsApi.listAttendance(training.id),
    ]);
    const byPlayer: Record<string, Attendance> = {};
    const nextMarks: Record<string, Mark> = {};
    for (const a of attendance ?? []) {
      byPlayer[a.player_id] = a;
      nextMarks[a.player_id] = markOf(a);
    }
    setRecords(byPlayer);
    setMarks(nextMarks);
    // A child who has left the club still shows where they have a mark.
    setPlayers((playerList ?? []).filter((p) => p.is_active || byPlayer[p.id]).sort(byName));
  }, [groupID, training.id]);

  useEffect(() => {
    panelRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    load()
      .catch((err) => {
        console.error("Failed to load attendance", err);
        setError(err?.message || "Не удалось загрузить посещаемость");
      })
      .finally(() => setLoading(false));
  }, [load]);

  const markFor = (playerID: string) => marks[playerID] ?? ABSENT;

  const changed = players.filter((p) => {
    const now = markFor(p.id);
    const before = markOf(records[p.id]);
    return now.attended !== before.attended || now.points !== before.points;
  });
  const present = players.filter((p) => markFor(p.id).attended).length;

  const setMark = (playerID: string, mark: Mark) => {
    setSaved(false);
    setMarks((m) => ({ ...m, [playerID]: mark }));
  };

  // The points endpoint recalculates the children's totals but also sets
  // attended = points > 0, so it only fits a present child getting points.
  // Everything else goes through the batch, which writes both fields as sent
  // and leaves the totals alone — hence the explicit recalculation after it.
  const save = async () => {
    const batch: AttendanceWrite[] = [];
    const pointChanges: { id: string; points: number }[] = [];
    for (const p of changed) {
      const mark = markFor(p.id);
      const record = records[p.id];
      if (record && record.attended && mark.attended && mark.points > 0) {
        pointChanges.push({ id: record.id, points: mark.points });
      } else {
        batch.push({ player_id: p.id, session_id: training.id, attended: mark.attended, points: mark.points });
      }
    }
    setBusy(true);
    setError(null);
    try {
      if (batch.length > 0) await trainingsApi.saveAttendance(batch);
      for (const c of pointChanges) await trainingsApi.setPoints(c.id, c.points);
      if (batch.length > 0) await trainingsApi.recalculatePoints();
      await load();
      setSaved(true);
    } catch (err: any) {
      console.error("Attendance save failed", err);
      setError(err?.message || "Не удалось сохранить посещаемость");
    } finally {
      setBusy(false);
    }
  };

  const meta = [
    weekdayOf(training.date).name,
    formatDay(training.date),
    formatTimeRange(training.start_time, training.end_time),
    training.place_name || training.title,
  ]
    .filter(Boolean)
    .join(", ");

  let content: React.ReactNode;
  if (!groupID) {
    content = <p className="mav-status-muted">У тренировки нет группы — отмечать некого.</p>;
  } else if (loading) {
    content = (
      <p className="mav-status-muted" role="status">
        Загрузка…
      </p>
    );
  } else if (players.length === 0) {
    content = error ? null : <p className="mav-status-muted">В группе пока нет детей.</p>;
  } else {
    content = (
      <>
        <div className="table-wrap">
          <table className="settings-table attendance-table" aria-label="Дети группы">
            <thead>
              <tr>
                <th>Ребёнок</th>
                <th>Был</th>
                <th>Баллы</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => {
                const mark = markFor(p.id);
                return (
                  <tr key={p.id}>
                    <td className="cell-attendance-name">
                      <span>{p.name}</span>
                      {!p.is_active && <span className="mav-badge mav-badge-muted">Неактивен</span>}
                    </td>
                    <td className="cell-attendance-present">
                      <input
                        type="checkbox"
                        className="attendance-check"
                        aria-label={`Был: ${p.name}`}
                        checked={mark.attended}
                        disabled={busy}
                        onChange={() => setMark(p.id, mark.attended ? ABSENT : { attended: true, points: mark.points })}
                      />
                    </td>
                    <td className="cell-attendance-points">
                      <input
                        type="number"
                        className="mav-input attendance-points"
                        aria-label={`Баллы: ${p.name}`}
                        min={0}
                        max={MAX_POINTS}
                        step={1}
                        value={mark.points}
                        disabled={busy || !mark.attended}
                        onChange={(e) => setMark(p.id, { attended: true, points: clampPoints(e.target.value) })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="training-panel-footer">
          <button type="button" className="mav-btn-primary" disabled={busy || changed.length === 0} onClick={save}>
            Сохранить посещаемость
          </button>
          <span className="attendance-count">{`Были ${present} из ${players.length}`}</span>
          {saved && changed.length === 0 && <span className="attendance-saved">Сохранено</span>}
        </div>
      </>
    );
  }

  return (
    <section ref={panelRef} className="training-panel" aria-label={`Посещаемость ${formatDay(training.date)}`}>
      <div className="training-panel-header">
        <div>
          <h3>Посещаемость</h3>
          <p className="training-panel-meta">{meta}</p>
        </div>
        <button type="button" className="mav-btn-secondary" onClick={onClose}>
          Закрыть
        </button>
      </div>
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
      {content}
    </section>
  );
}
